import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { SUPPORT_EMAIL as supportEmail } from '../../lib/supportContact';
const accountDeletionUrl = '/account-deletion';

export default function PrivacyPolicyPage() {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              Privacy Policy
            </Typography>
            <Typography color="text.secondary">
              Last updated: September 9, 2026
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              This Privacy Policy explains how Makeen Real Estate Management
              ("Makeen", "we", "us", or "our") collects, uses, stores, shares,
              retains, and deletes information for property management accounts.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Information we collect
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
              <li>Account details, including name, email address, phone number, role, and profile information.</li>
              <li>Property, owner, tenant, buyer, manager, accountant, maintenance, contract, and document records entered into the service.</li>
              <li>Financial records, invoices, vouchers, payment statuses, and reporting data.</li>
              <li>Photos, files, profile images, bid messages and responses, reports of inappropriate content, and support communications you choose to provide.</li>
              <li>Account and app activity, including sign-in events, notification read status, recorded business actions, and search terms sent to the server when you use search features.</li>
              <li>Authentication, session and network identifiers, including IP addresses, and device/browser information used to operate and protect the service. Our infrastructure providers may derive approximate location, such as country or city, from an IP address. The app does not request GPS location.</li>
              <li>Server request and security logs, including request paths, query parameters, response status and timing, used to operate, troubleshoot and secure the service. These records are retained by our infrastructure providers. The app's local error and crash logs stay on your device and are not automatically uploaded through a crash-reporting SDK.</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              How we use information
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
              <li>Provide property management, role-based access, document, reporting, and notification features.</li>
              <li>Process account, maintenance, contract, invoice, and support requests.</li>
              <li>Send service, security, account, and operational notifications.</li>
              <li>Improve reliability, performance, security, and user experience.</li>
              <li>Comply with legal, tax, accounting, audit, and regulatory obligations.</li>
              <li>Prevent misuse, fraud, unauthorized access, and data loss.</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Sharing and service providers
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              We do not sell personal information. We share information only as
              needed to operate the service, follow user permissions, or meet
              legal obligations. This may include Supabase for database,
              authentication, storage, and Edge Function infrastructure; Expo,
              Apple, and Google for app distribution; Vercel for hosting the web
              version; authorized property managers, owners, tenants, buyers,
              accountants, and administrators according to in-app permissions;
              and authorities or professional advisers when required by law,
              contract, tax, accounting, security, or dispute obligations. We do
              not use advertising, analytics, or crash-reporting SDKs.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Storage, security, and retention
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              We use technical and organizational safeguards appropriate for a
              property management service, including TLS protection for data in
              transit, managed cloud storage protections, Supabase
              authentication, and role-based access controls with row-level
              security for account, property, contract, financial, and document
              records. Profile and maintenance photos are private, with access
              restricted to the uploader and authorized staff or maintenance
              participants through expiring links. Property listing images remain
              public to anyone with their link; do not include sensitive information
              in property listing images. Content and user reports include the
              reporting account identifier and moderation outcomes and are used
              to investigate abuse.
              We retain information while needed to provide the service, maintain
              property and business records, comply with legal and accounting
              duties, resolve disputes, prevent fraud, and enforce agreements.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Your choices and account deletion
            </Typography>
            <Typography component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
              <li>You may access and correct account information available in the app.</li>
              <li>You may manage notification permissions in the app or device settings.</li>
              <li>You may request account deletion in the app from Profile &gt; Delete Account.</li>
              <li>You may also request deletion from <Link href={accountDeletionUrl}>our account deletion page</Link>.</li>
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
              When you delete your account from the app or from our account
              deletion page, your sign-in credentials, profile, contact details,
              and profile photo are removed immediately and you are signed out.
              Some property, contract, invoice, payment, tax, accounting,
              security, and dispute records may be retained only as required for
              legal, financial, fraud prevention, or business-record obligations,
              without your personal details.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Children
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Makeen is not intended for children under 16. We do not knowingly
              collect personal information from children under 16. If we learn
              that such information was collected, we will take steps to delete it.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              International processing
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Information may be processed and stored in countries other than
              your own through cloud, hosting, app distribution, and support
              providers. We use safeguards appropriate to those transfers.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Contact
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
              For privacy questions or data protection requests, use Contact
              Support in the app{supportEmail ? <> or email <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link></> : null}.
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            See also <Link href="/terms">Terms of Service</Link> and{' '}
            <Link href={accountDeletionUrl}>Account Deletion</Link>.
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}
